import React from 'react';
import { SectionTitle } from './SectionTitle';
import { ScrollAnimation } from './ScrollAnimation';
import { SpotlightCard } from './SpotlightCard';
import { MusicHeartIcon } from './icons/MusicHeartIcon';
import { SparkleIcon } from './icons/SparkleIcon';
import { BrainIcon } from './icons/BrainIcon';
import { EarIcon } from './icons/EarIcon';
import { MusicSheetIcon } from './icons/MusicSheetIcon';
import { PencilRulerIcon } from './icons/PencilRulerIcon';
import { useTranslation } from '../i18n/LanguageContext';

interface MasteryItemProps {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
}

const icons = {
    favorites: MusicHeartIcon,
    improvise: SparkleIcon,
    theory: BrainIcon,
    byEar: EarIcon,
    readMusic: MusicSheetIcon,
    arrange: PencilRulerIcon,
};

const MasteryCard: React.FC<MasteryItemProps> = ({ icon: Icon, title, description }) => (
  <SpotlightCard className="bg-brand-primary/60 p-6 rounded-lg shadow-xl border border-slate-700/50 hover:border-brand-accent/40 transition-all duration-300 transform hover:-translate-y-1 h-full flex flex-col text-center items-center">
    <div className="w-14 h-14 bg-gradient-to-br from-gradient-start/20 to-gradient-end/20 rounded-full flex items-center justify-center mb-4 flex-shrink-0">
        <Icon className="w-8 h-8 text-brand-accent" />
    </div>
    <h3 className="text-lg sm:text-xl font-serif font-semibold text-brand-light mb-2">{title}</h3>
    <p className="text-brand-text text-sm flex-grow">{description}</p>
  </SpotlightCard>
);

export const MasterySection: React.FC = () => {
  const { t } = useTranslation();
  const masteryItems = t('mastery.items') as { key: keyof typeof icons; title: string; description: string }[];

  return (
    <section id="habilidades" className="py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="md:grid md:grid-cols-3 md:gap-16 lg:gap-24 items-start">
            {/* --- Left Column (Sticky) --- */}
            <div className="md:col-span-1 md:sticky md:top-24">
                <ScrollAnimation animation="slideInUp">
                      <SectionTitle
                        title={t('mastery.title')}
                        subtitle={t('mastery.subtitle')}
                        className="text-left mb-12 md:mb-0"
                        titleClassName="text-4xl sm:text-5xl font-serif font-bold text-brand-light mb-4"
                        subtitleClassName="text-base sm:text-lg text-brand-text max-w-md mx-0"
                      />
                </ScrollAnimation>
            </div>

            {/* --- Right Column (Scrollable Cards) --- */}
            <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {masteryItems.map((item, index) => {
                    const Icon = icons[item.key];
                    return (
                        <ScrollAnimation key={index} animation="scaleIn" delay={150 + index * 50}>
                            <MasteryCard icon={Icon} title={item.title} description={item.description} />
                        </ScrollAnimation>
                    );
                  })}
            </div>
        </div>
      </div>
    </section>
  );
};